import {
  Component,
  OnDestroy,
  ElementRef,
  Inject,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LocationService, LocationSource, OrderLocation, PopularCity } from '../../data/location.service';
import { LocationDriftService } from '../../data/location-drift.service';
import { I18nService } from '../../../../Shared/i18n/i18n.service';
import { BikeLoaderComponent } from '../../../../Shared/bike-loader/bike-loader';
import { AddressService } from '../../data/address.service';
import { CustomerAuthService } from '../../data/customer-auth.service';
import { CartService } from '../../data/cart.service';
import { CustomerAddress } from '../../data/address.models';
import { StoreService } from '../../data/store.service';
import { Store, StoreDeliveryZone } from '../../data/store.models';
import { parseGeoJsonRings } from '../../data/geo';
import { ZoneVerdict, isCrossZoneEntry, zoneVerdict } from '../../data/delivery-rules';
import { PLATFORM_COMPANY } from '../../data/ordering-api';
import { MAP_TILE_URL, MAP_TILE_OPTIONS } from '../../../../Shared/map-tiles';

/** Structured address resolved from a map pin (street/area/city), used as the saved address name. */
interface PinGeo {
  name: string;
  road: string;
  city: string;
  state: string | null;
  postcode: string | null;
  country: string | null;
}

/** Up to this many saved addresses per customer (foodpanda-style). */
const MAX_ADDRESSES = 3;

/**
 * Location + address picker (foodpanda-style) — the ONE place to set where an order is delivered.
 * Pick by: a saved address (stored in CRM, shown in the list + as pins), address search, "Locate me",
 * a popular city, or tapping/dragging the map pin. Listing AND saving work for any signed-in customer,
 * shop open or not: with a shop the address lands in that merchant's book, without one in the
 * customer's platform-level book, and the list merges every book they have. Whenever a pin is set it is
 * reverse-geocoded (OSM
 * Nominatim) so the shown name is the REAL address (street, area, city), not "Your current location".
 * A pin can be saved (max 3). Confirm commits the chosen location + saved address to LocationService.
 *
 * State lives in signals so async callbacks (geolocation, fetch, Leaflet) re-render under zoneless CD.
 */
@Component({
  selector: 'app-location-modal',
  standalone: true,
  imports: [BikeLoaderComponent, CommonModule, FormsModule],
  templateUrl: './location-modal.html',
  styleUrls: ['./location-modal.css'],
})
export class LocationModalComponent implements OnDestroy {
  /** The progress ticker outlives a closed modal otherwise — it is an interval, not a subscription. */
  ngOnDestroy(): void {
    this.stopLocateProgress();
  }

  @ViewChild('pickMap') private mapEl?: ElementRef<HTMLDivElement>;

  protected readonly loc = inject(LocationService);
  protected readonly i18n = inject(I18nService);
  /** Drives the "your location has changed" banner, and is re-armed on confirm. */
  protected readonly drift = inject(LocationDriftService);

  /** Metres as a shopper reads them: "250 m away" is useful, "1247 m away" is not. */
  driftLabel(metres: number): string {
    return metres >= 1000
      ? this.i18n.t('location.awayKm', { km: (metres / 1000).toFixed(1) })
      : this.i18n.t('location.awayM', { m: metres });
  }

  protected readonly auth = inject(CustomerAuthService);
  private readonly addressApi = inject(AddressService);
  private readonly cart = inject(CartService);
  private readonly router = inject(Router);
  private readonly isBrowser: boolean;

  readonly maxAddresses = MAX_ADDRESSES;
  query = '';
  readonly searching = signal(false);
  readonly locating = signal(false);
  readonly error = signal('');

  // ── "Locate me" progress ────────────────────────────────────────────────────
  // Finding someone takes two steps that BOTH stall on a phone indoors: the GPS fix, then the
  // reverse-geocode that turns it into a street name. Before this, the only cue was the button
  // relabelling to "Locating…", which after four silent seconds looks identical to a dead button.
  //
  // The two phases are shown differently on purpose, because we know a deadline for one and not the
  // other: the fix has a real 10s timeout, so its bar is DETERMINATE and means something. The address
  // lookup is an ordinary fetch with no deadline we control, so it gets an indeterminate stripe rather
  // than a fake countdown.

  /** Geolocation's own timeout — the bar below measures against it, so they must not drift apart. */
  private static readonly LOCATE_TIMEOUT_MS = 10_000;

  /** 0–100 while acquiring the GPS fix. Capped below 100: a full bar that keeps waiting reads as stuck. */
  readonly locateProgress = signal(0);
  /** True during the reverse-geocode step, after a fix has landed. */
  readonly resolvingAddress = signal(false);
  private locateTicker?: ReturnType<typeof setInterval>;
  readonly pending = signal<OrderLocation | null>(null);

  // Saved addresses (server-side, CRM)
  readonly addresses = signal<CustomerAddress[]>([]);
  readonly addressesLoading = signal(false);
  /** The last load failed — shown as such, because a silent empty list reads as "nothing was saved". */
  readonly addressesFailed = signal(false);
  readonly selectedAddressId = signal<string | null>(null);
  readonly savingAddress = signal(false);
  /** Address currently being deleted, so its row can show progress and can't be tapped twice. */
  readonly removingId = signal<string | null>(null);

  /** The 3 address categories a customer can save under (foodpanda-style) — one each, API-enforced. */
  readonly labelOptions = ['Home', 'Work', 'Other'] as const;
  /** Category chosen for the pin being saved. */
  readonly pendingLabel = signal<string | null>(null);
  /** Editable street line for the pin being saved (house #, street) — prefilled from the
   * reverse-geocoded road, refined by the customer before saving (foodpanda-style). */
  readonly pendingStreet = signal('');
  /** Optional second address line — floor / unit / apartment details. Maps to `street2`. */
  readonly pendingStreet2 = signal('');
  /** Optional note to the rider (building, landmark…). Maps to `deliveryInstructions`. */
  readonly pendingNote = signal('');

  /** Structured address for the current pin (from reverse-geocode) — used when saving. */
  private pendingGeo: PinGeo | null = null;

  /** Where the pending spot came from. Travels with it to LocationService, which keeps a city across
   *  sign-out but drops an address / located pin (both carry the customer's real street). */
  private pendingSource: LocationSource = 'pin';

  private map: any;
  private marker: any;
  private addressMarkers: any[] = [];
  private zoneLayers: any[] = [];

  // ── Delivery-area (geofence) preview for the cart's store ─────────────────────
  private readonly storeApi = inject(StoreService);
  /** The cart store's delivery zones — the ONLY truth for delivery. Drawn on the map + used for
   * the instant in/out check. An empty list (once loaded) means the store is pickup-only. */
  readonly zones = signal<StoreDeliveryZone[]>([]);
  readonly zonesLoaded = signal(false);
  /** The cart store itself (name for the in/out messages). */
  readonly zoneStore = signal<Store | null>(null);
  /** Which store the zones/store signals were loaded for (avoids reload per open). */
  private zonesForStore = '';

  /** Where the pin falls among the store's zones: one of its own, a zone it reaches over a delivery route
   * (cross-zone), or none of them. 'none' without a real pin or without zones. */
  readonly verdict = computed<ZoneVerdict>(() => {
    const p = this.pending();
    if (!p || (p.lat === 0 && p.lng === 0)) return { kind: 'none' };
    return zoneVerdict(this.zones(), p.lat, p.lng);
  });

  /** Instant client-side mirror of the server's delivery-area check: the pin must fall inside one
   * of the store's zone fences. (No radius fallback — zones are the only truth.) */
  readonly outsideDeliveryArea = computed(() => this.verdict().kind === 'outside');

  /** The store sits outside every delivery zone → customers can only pick up from it. */
  readonly pickupOnly = computed(() =>
    this.zonesLoaded() && this.zones().length === 0 && !!this.cart.storeId());

  /** The store doesn't deliver at all (pickup-only by configuration), whatever its zones say. */
  readonly storeHasNoDelivery = computed(() => this.zoneStore()?.hasDelivery === false);

  /** Whether a service-area overlay is being shown. */
  readonly hasZoneOverlay = computed(() => this.zones().length > 0);

  /** A real pin is set (not the 0,0 placeholder of a coordinate-less saved address). */
  readonly hasRealPin = computed(() => {
    const p = this.pending();
    return !!p && !(p.lat === 0 && p.lng === 0);
  });

  /** A saved address with no map pin is selected. Confirming it would commit (0,0) as the delivery
   *  spot — which reads as "somewhere off the coast of Africa": the nearby-stores list empties and
   *  checkout refuses the order as out-of-area. The customer has to place the pin first. */
  readonly needsPin = computed(() => !!this.pending() && !this.hasRealPin());

  zoneStoreName(): string {
    return this.zoneStore()?.tradingName || this.i18n.t('location.theStore');
  }

  /** "✓ Cafe One delivers here from Jinnah Garden (PKR 250.00)." for a pin the store reaches over a
   *  route; '' otherwise. The route's free-delivery threshold isn't applied — checkout quotes the real fee. */
  routeOkText(): string {
    const v = this.verdict();
    if (v.kind !== 'cross' || !v.originZoneName) return '';
    const params = { store: this.zoneStoreName(), origin: v.originZoneName };
    return v.fee > 0
      ? this.i18n.t('location.deliversHereRoute', { ...params, fee: this.formatFee(v.fee) })
      : this.i18n.t('location.deliversHereRouteFree', params);
  }

  /** Money as checkout shows it: "PKR 250.00", or the bare amount when the cart has no currency. */
  private formatFee(fee: number): string {
    const amount = fee.toFixed(2);
    const cc = this.cart.currencyCode();
    return cc ? `${cc} ${amount}` : amount;
  }

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    effect(() => {
      const open = this.loc.pickerOpen();
      // Track auth + store so the saved-address list reloads if they change while the picker is open.
      this.auth.isAuthenticated();
      this.cart.storeId();
      if (!this.isBrowser) return;
      if (!open) {
        // The dialog (and its #pickMap element) is removed by @if when closed — tear the map down so
        // the next open rebuilds it on the fresh element (otherwise the map is blank until a refresh).
        this.destroyMap();
        return;
      }
      this.loadAddresses();
      this.loadDeliveryArea();
      // Initialise the pin + map once per open (the map is rebuilt on a brand-new container each time).
      if (!this.map) {
        this.error.set('');
        this.pending.set(this.loc.location());
        this.pendingSource = this.loc.location()?.source ?? 'pin';
        this.selectedAddressId.set(this.loc.selectedAddress()?.id ?? null);
        this.pendingLabel.set(null); // start each open unlabelled (don't reuse a category from a prior session)
        this.pendingStreet.set(''); // and with a fresh street line
        this.pendingStreet2.set('');
        this.pendingNote.set('');
        setTimeout(() => this.initMap(), 60); // wait for the dialog to render
      }
    });
  }

  /** The shop in the cart, if any — the merchant whose book a new address goes to. */
  get storeId(): string {
    return this.cart.storeId();
  }

  /** Signed in → the saved book can be listed (store or not — see AddressService.list). */
  get canListAddresses(): boolean {
    return this.auth.isAuthenticated();
  }

  /** Signed in → the customer can save an address, shop open or not. With a shop it lands in that
   *  merchant's book; without one, in their platform-level book. Both list everywhere. */
  get canSaveAddresses(): boolean {
    return this.auth.isAuthenticated();
  }

  /** Whether another address can still be saved (cap of MAX_ADDRESSES). */
  get canSaveMore(): boolean {
    return this.addresses().length < MAX_ADDRESSES;
  }

  close(): void {
    this.loc.closePicker();
  }

  // ── Delivery area (geofence) overlay ─────────────────────────────────────────
  /** Load the cart store's zones + header once per store, for the overlay and the in/out check. */
  private loadDeliveryArea(): void {
    const storeId = this.cart.storeId();
    if (!storeId) {
      this.zonesForStore = '';
      this.zones.set([]);
      this.zonesLoaded.set(false);
      this.zoneStore.set(null);
      return;
    }
    if (this.zonesForStore === storeId) return;
    this.zonesForStore = storeId;
    this.zones.set([]);
    this.zonesLoaded.set(false);
    this.zoneStore.set(null);
    this.storeApi.getDeliveryZones(storeId).subscribe({
      next: (res) => {
        this.zones.set(res.success ? res.data ?? [] : []);
        this.zonesLoaded.set(res.success);
        this.renderZoneLayers();
      },
      error: () => this.zones.set([]),
    });
    this.storeApi.getStore(storeId).subscribe({
      next: (res) => this.zoneStore.set(res.success ? res.data : null),
      error: () => this.zoneStore.set(null),
    });
  }

  /** Shade the store's delivery zones on the map (polygons/circles) — read-only layers under the
   * pin. Zones are the only truth; there is no store-radius fallback. A zone the store reaches over a
   * delivery route (cross-zone) is drawn dashed and lighter than its own zones. */
  private renderZoneLayers(): void {
    const L = (window as any).L;
    if (!this.map || !L) return;
    for (const l of this.zoneLayers) {
      try { this.map.removeLayer(l); } catch { /* noop */ }
    }
    this.zoneLayers = [];
    const homeStyle = { color: '#FA0E0E', weight: 2, fillColor: '#FA0E0E', fillOpacity: 0.07, interactive: false };
    const crossZoneStyle = {
      color: '#FA0E0E', weight: 2, dashArray: '6 6', fillColor: '#FA0E0E', fillOpacity: 0.03, interactive: false,
    };
    for (const z of this.zones()) {
      const style = isCrossZoneEntry(z) ? crossZoneStyle : homeStyle;
      const rings = parseGeoJsonRings(z.geoPolygon);
      if (rings.length > 0) {
        for (const ring of rings) {
          this.zoneLayers.push(L.polygon(ring.map((p) => [p.lat, p.lng]), style).addTo(this.map));
        }
      } else if (z.centerLatitude != null && z.centerLongitude != null && (z.radiusKm ?? 0) > 0) {
        this.zoneLayers.push(
          L.circle([z.centerLatitude, z.centerLongitude], { ...style, radius: (z.radiusKm as number) * 1000 }).addTo(this.map),
        );
      }
    }
  }

  // ── Saved addresses ──────────────────────────────────────────────────────────
  private loadAddresses(): void {
    if (!this.canListAddresses) {
      this.addresses.set([]);
      this.addressesFailed.set(false);
      return;
    }
    this.addressesLoading.set(true);
    const storeId = this.storeId || null;
    this.addressApi.list(storeId).subscribe({
      next: (res) => {
        const list = res.success ? res.data ?? [] : [];
        // A store-scoped book reads empty both for a customer who has saved nothing AND for one whose
        // addresses sit on another company — the store here comes from the cart, which can be left over
        // from an earlier session. Fall back to the whole book so a signed-in customer is never told
        // "no saved addresses" while their addresses exist.
        if (list.length === 0 && storeId) {
          // Only the FALLBACK failing means "the store's book really is empty" — the primary call
          // already succeeded, so don't cry error (an API without the store-less list answers 400).
          this.addressApi.list(null).subscribe({
            next: (all) => this.applyAddresses(all.success ? all.data ?? [] : [], true),
            error: () => this.applyAddresses([], true),
          });
          return;
        }
        this.applyAddresses(list, res.success);
      },
      error: () => this.applyAddresses([], false),
    });
  }

  /** Show the loaded book. A failed load is flagged, never rendered as an empty address book. */
  private applyAddresses(list: CustomerAddress[], ok: boolean): void {
    this.addressesLoading.set(false);
    this.addresses.set(list);
    this.addressesFailed.set(!ok);
    this.renderAddressMarkers();
  }

  /** The display name of a saved address = its category label (Home/Work/Other) or street+city. */
  addressName(a: CustomerAddress): string {
    return a.label || [a.street, a.city].filter(Boolean).join(', ') || 'Saved address';
  }

  /** Secondary line under the category — the real street (+ floor/unit) / area / city. */
  addressDetail(a: CustomerAddress): string {
    return [a.street, a.street2, a.city].filter(Boolean).join(', ');
  }

  /** A category already has a saved address (each of the 3 categories is used at most once). */
  isLabelUsed(l: string): boolean {
    return this.addresses().some((a) => (a.label ?? '').toLowerCase() === l.toLowerCase());
  }

  /** Pick (or toggle off) the category to save the current pin under. */
  pickLabel(l: string): void {
    if (this.isLabelUsed(l)) return;
    this.pendingLabel.set(this.pendingLabel() === l ? null : l);
  }

  /** The icon for a saved address's slot. */
  addressIcon(label?: string | null): string {
    return label === 'Home' ? '🏠' : label === 'Work' ? '💼' : '📍';
  }

  /**
   * Delete a saved address. Needed as much as saving: the book holds three slots, so without a way to
   * free one a customer who has filled them can never save a new address again.
   */
  removeAddress(a: CustomerAddress, event: Event): void {
    event.stopPropagation(); // the row itself selects the address
    if (this.removingId()) return;
    const name = this.addressName(a);
    if (this.isBrowser && !confirm(this.i18n.t('location.removeConfirm', { name }))) return;
    this.removingId.set(a.id);
    this.error.set('');
    this.addressApi.remove(a.id).subscribe({
      next: (res) => {
        this.removingId.set(null);
        if (!res.success) {
          this.error.set(res.message || this.i18n.t('location.removeFailed'));
          return;
        }
        if (this.selectedAddressId() === a.id) {
          this.selectedAddressId.set(null);
          this.loc.setSelectedAddress(null); // it no longer exists — don't ship an order against it
        }
        this.loadAddresses();
      },
      error: (e) => {
        this.removingId.set(null);
        this.error.set(e?.error?.message || this.i18n.t('location.removeFailed'));
      },
    });
  }

  selectAddress(a: CustomerAddress): void {
    const name = this.addressName(a);
    this.pendingGeo = null;
    this.pendingLabel.set(null); // picking an existing address isn't a save — clear any pending category
    if (a.latitude != null && a.longitude != null) {
      this.setPending({ label: name, lat: a.latitude, lng: a.longitude }, a.id, 'address');
    } else {
      this.pending.set({ label: name, lat: 0, lng: 0 });
      this.pendingSource = 'address';
      this.selectedAddressId.set(a.id);
    }
  }

  /** Save the current map pin as a new address under the chosen category (Home/Work/Other). */
  saveCurrentPin(): void {
    const p = this.pending();
    if (!p || !this.canSaveAddresses || this.savingAddress()) return;
    if (!this.canSaveMore) {
      this.error.set(this.i18n.t('location.saveMaxError', { max: MAX_ADDRESSES }));
      return;
    }
    const label = this.pendingLabel();
    if (!label) {
      this.error.set(this.i18n.t('location.chooseLabel'));
      return;
    }
    const street = this.pendingStreet().trim();
    if (!street) {
      this.error.set(this.i18n.t('location.streetRequired'));
      return;
    }
    this.savingAddress.set(true);
    this.error.set('');
    const g = this.pendingGeo;
    this.addressApi
      .create({
        storeId: this.storeId || null, // no shop open → the platform-level book
        // Fallback the API only consults for accounts with no platform stamp of their own.
        platformCompanyId: PLATFORM_COMPANY.companyId || null,
        label, // the category (Home/Work/Other); street/city carry the real address
        street, // the customer's typed street line (prefilled from the pin's reverse-geocode)
        street2: this.pendingStreet2().trim() || null, // floor / unit / apartment details
        deliveryInstructions: this.pendingNote().trim() || null, // note to rider
        city: g?.city ?? '',
        state: g?.state ?? null,
        postalCode: g?.postcode ?? '',
        country: g?.country ?? '',
        latitude: p.lat,
        longitude: p.lng,
        isDefault: this.addresses().length === 0,
      })
      .subscribe({
        next: (res) => {
          this.savingAddress.set(false);
          if (res.success && res.data) {
            this.addresses.update((list) => [...list, res.data]);
            this.loadAddresses(); // re-read, so what's shown is what the server actually holds
            this.pendingLabel.set(null);
            this.pendingStreet2.set('');
            this.pendingNote.set('');
            this.renderAddressMarkers();
            this.selectedAddressId.set(res.data.id);
          } else {
            this.error.set(res.message || this.i18n.t('location.saveFailed'));
          }
        },
        error: (e) => {
          this.savingAddress.set(false);
          this.error.set(e?.error?.message || this.i18n.t('location.saveFailed'));
        },
      });
  }

  // ── Pick by search / locate / city / pin ───────────────────────────────────────

  /**
   * Drop the pin at a raw spot (locate me / map tap / marker drag) and resolve its address.
   * Clearing pendingGeo FIRST matters: setPending seeds the editable street line from it, so without
   * this the second pin inherited the first pin's street — and resolveAndLabel then left it alone
   * (it only fills a blank field), saving one street with another spot's coordinates.
   */
  private dropPin(lat: number, lng: number): void {
    this.pendingGeo = null;
    this.setPending({ label: this.i18n.t('location.locatingAddress'), lat, lng });
    this.resolveAndLabel(lat, lng);
  }

  locateMe(): void {
    if (!this.isBrowser || !('geolocation' in navigator)) {
      this.error.set(this.i18n.t('location.unavailable'));
      return;
    }
    this.locating.set(true);
    this.error.set('');
    this.startLocateProgress();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.stopLocateProgress();
        this.locating.set(false);
        this.dropPin(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        this.stopLocateProgress();
        this.locating.set(false);
        this.error.set(this.i18n.t('location.failed'));
      },
      {
        enableHighAccuracy: false,
        timeout: LocationModalComponent.LOCATE_TIMEOUT_MS,
        maximumAge: 60000,
      },
    );
  }

  /** Advance the bar against the real timeout, so it reaches the end just as the attempt gives up. */
  private startLocateProgress(): void {
    this.stopLocateProgress();
    const startedAt = Date.now();
    this.locateProgress.set(0);
    this.locateTicker = setInterval(() => {
      const pct = ((Date.now() - startedAt) / LocationModalComponent.LOCATE_TIMEOUT_MS) * 100;
      // Stop at 96%: the last stretch belongs to whatever actually resolves, and a bar sitting at 100
      // while still spinning is the thing this was added to avoid.
      this.locateProgress.set(Math.min(96, pct));
    }, 100);
  }

  private stopLocateProgress(): void {
    if (this.locateTicker) clearInterval(this.locateTicker);
    this.locateTicker = undefined;
    this.locateProgress.set(0);
  }

  search(): void {
    const q = this.query.trim();
    if (!q || !this.isBrowser) return;
    this.searching.set(true);
    this.error.set('');
    fetch(
      `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&addressdetails=1&q=${encodeURIComponent(q)}`,
      { headers: { Accept: 'application/json' } },
    )
      .then((r) => r.json())
      .then((arr: any[]) => {
        this.searching.set(false);
        if (arr && arr.length > 0) {
          const j = arr[0];
          this.pendingGeo = this.fmtGeo(j);
          this.setPending({ label: this.pendingGeo.name, lat: +j.lat, lng: +j.lon });
        } else {
          this.error.set(this.i18n.t('location.searchNoMatch'));
        }
      })
      .catch(() => {
        this.searching.set(false);
        this.error.set(this.i18n.t('location.searchFailed'));
      });
  }

  pickCity(c: PopularCity): void {
    this.pendingGeo = { name: c.name, road: c.name, city: c.name, state: null, postcode: null, country: null };
    this.setPending({ label: c.name, lat: c.lat, lng: c.lng }, null, 'city');
  }

  confirm(): void {
    const p = this.pending();
    if (!p) return;
    if (this.needsPin()) {
      this.error.set(this.i18n.t('location.needsPin'));
      return;
    }
    const chosen = this.addresses().find((a) => a.id === this.selectedAddressId()) ?? null;
    const goBrowsing = this.loc.navigateOnConfirm;
    this.loc.setSelectedAddress(chosen);
    // Stamp where this spot came from: anything but a popular city is the customer's own street and
    // gets dropped at sign-out instead of greeting whoever uses this device next.
    this.loc.setLocation({ ...p, source: chosen ? 'address' : this.pendingSource });
    // Re-arm the drift watcher against this new spot, so the NEXT journey is noticed too.
    this.drift.reset();
    this.loc.closePicker();
    // Opened from the header to choose where to shop → take the shopper to the landing page.
    // (When opened mid-checkout to pick a delivery address, this flag is false and we stay put.)
    if (goBrowsing) this.router.navigate(['/discover']);
  }

  // ── reverse-geocode (client-side OSM Nominatim — no auth needed; gives a real address name) ──
  private resolveAndLabel(lat: number, lng: number): void {
    this.pendingGeo = null;
    this.resolvingAddress.set(true);
    fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`, {
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((j) => {
        const g = this.fmtGeo(j);
        this.pendingGeo = g;
        const cur = this.pending();
        if (cur && cur.lat === lat && cur.lng === lng) {
          this.pending.set({ ...cur, label: g.name });
          // Prefill the editable street line from the resolved road — unless the customer already
          // started typing their own while the lookup was in flight.
          if (!this.pendingStreet().trim()) this.pendingStreet.set(g.road || g.name);
        }
      })
      .catch(() => {
        /* keep the placeholder label — saving still works with coordinates */
      })
      .finally(() => this.resolvingAddress.set(false));
  }

  /** Build "street, area, city" + structured fields from a Nominatim result. */
  private fmtGeo(j: any): PinGeo {
    const a = j?.address ?? {};
    const road = a.road ?? a.pedestrian ?? a.footway ?? '';
    const area = a.neighbourhood ?? a.suburb ?? a.quarter ?? '';
    const city = a.city ?? a.town ?? a.village ?? a.municipality ?? a.county ?? '';
    const parts: string[] = [];
    for (const x of [road, area, city]) if (x && !parts.includes(x)) parts.push(x);
    const name = parts.join(', ') || j?.display_name || 'Selected location';
    return {
      name,
      road: road || area || city || name,
      city: city || area,
      state: a.state ?? null,
      postcode: a.postcode ?? null,
      country: a.country ?? null,
    };
  }

  // ── internals ──
  /** Set the pending location. A null addressId means a raw pick (clears any saved-address selection). */
  private setPending(loc: OrderLocation, addressId: string | null = null, source: LocationSource = 'pin'): void {
    this.pending.set(loc);
    this.pendingSource = source;
    this.selectedAddressId.set(addressId);
    this.pendingLabel.set(null); // a new pin starts unlabelled — the user picks a category to save it
    // Street line follows the pin: search/city picks carry a geocode already; raw map picks clear it
    // and resolveAndLabel refills once the reverse-geocode lands.
    this.pendingStreet.set(this.pendingGeo?.road ?? '');
    this.recenter();
  }

  private initMap(): void {
    const L = (window as any).L;
    if (!L || !this.mapEl) return;
    this.destroyMap(); // the @if recreated the container — always start from a fresh map
    try {
      const p0 = this.pending();
      const hasPin = !!p0 && !(p0.lat === 0 && p0.lng === 0);
      const start = hasPin ? p0! : { lat: 30.3753, lng: 69.3451 };
      this.map = L.map(this.mapEl.nativeElement, { zoomControl: true })
        .setView([start.lat, start.lng], hasPin ? 14 : 5);
      L.tileLayer(MAP_TILE_URL, MAP_TILE_OPTIONS).addTo(this.map);
      this.map.on('click', (e: any) => this.dropPin(e.latlng.lat, e.latlng.lng));
      if (hasPin) this.placeMarker(p0!.lat, p0!.lng);
      this.renderAddressMarkers();
      this.renderZoneLayers();
      setTimeout(() => { try { this.map.invalidateSize(); } catch { /* noop */ } }, 80);
    } catch {
      this.map = undefined;
    }
  }

  /** Remove the Leaflet map + markers (the dialog's container is recreated each open by @if). */
  private destroyMap(): void {
    if (this.map) {
      try { this.map.remove(); } catch { /* noop */ }
      this.map = undefined;
    }
    this.marker = undefined;
    this.addressMarkers = [];
    this.zoneLayers = [];
  }

  private recenter(): void {
    const p = this.pending();
    if (!this.map || !p || (p.lat === 0 && p.lng === 0)) return;
    this.map.setView([p.lat, p.lng], 14);
    this.placeMarker(p.lat, p.lng);
  }

  private placeMarker(lat: number, lng: number): void {
    const L = (window as any).L;
    if (!this.map || !L) return;
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
      return;
    }
    this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map);
    this.marker.on('dragend', () => {
      const pt = this.marker.getLatLng();
      this.dropPin(pt.lat, pt.lng);
    });
  }

  /** Small pins for each saved address that has coordinates — tap to select it. */
  private renderAddressMarkers(): void {
    const L = (window as any).L;
    if (!this.map || !L) return;
    for (const m of this.addressMarkers) {
      try { this.map.removeLayer(m); } catch { /* noop */ }
    }
    this.addressMarkers = [];
    for (const a of this.addresses()) {
      if (a.latitude == null || a.longitude == null) continue;
      const m = L.circleMarker([a.latitude, a.longitude], {
        radius: 7, color: '#111', weight: 2, fillColor: '#FA0E0E', fillOpacity: 0.9,
      }).addTo(this.map);
      m.on('click', () => this.selectAddress(a));
      this.addressMarkers.push(m);
    }
  }
}
